import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Maximize2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Reveal, SceneHead, routesFor, type Lang } from "../primitives";
import amazonSales from "@/assets/case-studies/amazon-sales-dashboard.png";
import amazonProducts from "@/assets/case-studies/amazon-abrasives-products.jpg";
import venadoProducts from "@/assets/case-studies/venado-products-crop.jpg";
import venadoStore from "@/assets/case-studies/venado-store-crop.jpg";

const t = {
  es: {
    aria: "Casos",
    label: "Casos",
    title: "Decisiones concretas. Trabajo que puedes revisar.",
    intro:
      "Dos proyectos con alcance distinto: uno de venta en marketplace y otro de identidad, producto y presencia digital. Cada imagen corresponde a trabajo real y se explica en su pie.",
    c1kicker: "Caso 01",
    c1place: "Amazon USA",
    c1title: "Marca mexicana de abrasivos en Amazon Estados Unidos",
    c1problem: "Problema.",
    c1problemText:
      "Fabricante de lijas y discos abrasivos, sin presencia propia en el mercado estadounidense y con catálogo mal representado para un comprador que busca por uso, grano y medida.",
    c1decisions: "Decisiones.",
    c1decisionsText:
      "Investigación de demanda y competencia, reestructuración de listings, diseño de imágenes y vídeo de producto, registro de marca (Brand Registry) y publicidad conectada a la intención de compra. Después, expansión a más mercados.",
    c1resultLabel: "Resultado reportado por Luis ROC",
    c1figure: "≈ $800/mes → $79,000/mes",
    c1figureNote: "Facturación mensual en Amazon USA, al mes 10 del proyecto.",
    c1disclaimer:
      "Dato proporcionado por el propietario del proyecto. Corresponde a facturación, no a beneficio neto, y no es una garantía ni un resultado atribuible solo al SEO: intervienen catálogo, precio, inventario, diseño y publicidad. Marca anonimizada por acuerdo con el cliente.",
    allCases: "Ver todos los casos",
    zoom: "Ampliar",
    c1imgAlt:
      "Captura del panel de ventas de Amazon Seller Central del proyecto, con datos de cuenta anonimizados.",
    c1imgZoomAlt: "Captura ampliada del panel de ventas de Amazon Seller Central del proyecto.",
    c1imgZoomTitle: "Captura ampliada del panel de ventas de Amazon Seller Central",
    c1caption:
      "Evidencia principal: captura real de Seller Central conservando periodo, moneda y métrica tal como aparecen en la cuenta. El periodo mostrado es el acumulado histórico de la cuenta (2019–2021), no los diez meses citados: por sí sola no prueba ese plazo.",
    c1prodAlt:
      "Fotografía de producto de lijas y discos abrasivos preparada para las fichas de Amazon.",
    c1prodCaption:
      "Apoyo: fotografía de catálogo producida para las fichas, con producto, grano y medida legibles en miniatura.",
    c2kicker: "Caso 02",
    c2place: "Houston, Texas",
    c2title: "El Venado Foods: identidad, producto y presencia digital",
    c2lead: "Qué se desarrolló.",
    c2leadText:
      "Identidad de marca, creación del personaje Doña Chela, sistema de packaging, materiales para punto de venta, sitio web, tienda en línea e investigación SEO para el mercado hispano de Estados Unidos.",
    c2points: [
      "Un personaje propio para que la marca se reconozca de lejos en el anaquel, sin depender del nombre.",
      "Sistema de color y tipografía que distingue variedades dentro de una misma familia visual.",
      "Arquitectura del sitio y de la tienda organizada por producto y por búsqueda en español e inglés.",
    ],
    c2note:
      "Caso publicado con autorización. No se reportan cifras de ventas, conversión ni posiciones: no existe documentación verificable que compartir para este proyecto.",
    c2imgAlt:
      "Dos empaques de El Venado Foods —camarón entero y semillas de chía— con el personaje Doña Chela en el frente.",
    c2caption:
      "Composición principal: packaging desarrollado para la marca, con el personaje Doña Chela y un color por variedad dentro de una misma familia visual.",
    c2webAlt:
      "Página de inicio de la tienda en línea de El Venado Foods, con navegación por productos y recetas.",
    c2webCaption:
      "Apoyo: tienda en línea desarrollada para la marca, con navegación por producto y contenido de recetas.",
  },
  en: {
    aria: "Case studies",
    label: "Case studies",
    title: "Concrete decisions. Work you can review.",
    intro:
      "Two projects with different scope: one marketplace sales project and one covering identity, product and digital presence. Every image is real work and is explained in its caption.",
    c1kicker: "Case 01",
    c1place: "Amazon USA",
    c1title: "Mexican abrasives brand on Amazon United States",
    c1problem: "Problem.",
    c1problemText:
      "A manufacturer of sandpaper and abrasive discs with no presence of its own in the U.S. market and a catalogue poorly represented for buyers who search by use, grit and size.",
    c1decisions: "Decisions.",
    c1decisionsText:
      "Demand and competitor research, listing restructuring, product image and video design, Brand Registry, and advertising connected to purchase intent. Later, expansion into more markets.",
    c1resultLabel: "Result reported by Luis ROC",
    c1figure: "≈ $800/mo → $79,000/mo",
    c1figureNote: "Monthly revenue on Amazon USA, at month 10 of the project.",
    c1disclaimer:
      "Figure provided by the project owner. It refers to revenue, not net profit, and it is neither a guarantee nor a result attributable to SEO alone: catalogue, pricing, inventory, design and advertising all play a part. Brand anonymized by agreement with the client.",
    allCases: "See all case studies",
    zoom: "Enlarge",
    c1imgAlt:
      "Screenshot of the project's Amazon Seller Central sales panel, with account data anonymized.",
    c1imgZoomAlt: "Enlarged screenshot of the project's Amazon Seller Central sales panel.",
    c1imgZoomTitle: "Enlarged Amazon Seller Central sales panel screenshot",
    c1caption:
      "Main evidence: a real Seller Central screenshot keeping the period, currency and metric exactly as they appear in the account. The period shown is the account's historical total (2019–2021), not the ten months cited: on its own it does not prove that timeframe.",
    c1prodAlt: "Product photography of sandpaper and abrasive discs prepared for the Amazon listings.",
    c1prodCaption:
      "Support: catalogue photography produced for the listings, with product, grit and size legible at thumbnail size.",
    c2kicker: "Case 02",
    c2place: "Houston, Texas",
    c2title: "El Venado Foods: identity, product and digital presence",
    c2lead: "What was developed.",
    c2leadText:
      "Brand identity, creation of the Doña Chela character, packaging system, point-of-sale materials, website, online store and SEO research for the U.S. Hispanic market.",
    c2points: [
      "A character of its own so the brand is recognized from a distance on the shelf, without relying on the name.",
      "A colour and typography system that separates varieties inside one visual family.",
      "Site and store architecture organized by product and by search in Spanish and English.",
    ],
    c2note:
      "Case published with authorization. No sales, conversion or ranking figures are reported: there is no verifiable documentation to share for this project.",
    c2imgAlt:
      "Two El Venado Foods packages —whole shrimp and chia seeds— with the Doña Chela character on the front.",
    c2caption:
      "Main composition: packaging developed for the brand, with the Doña Chela character and one colour per variety inside a single visual family.",
    c2webAlt: "Home page of the El Venado Foods online store, with product and recipe navigation.",
    c2webCaption:
      "Support: online store developed for the brand, with product navigation and recipe content.",
  },
} as const;

/**
 * Dos casos como composiciones independientes. Cada <article> es un contexto de
 * apilamiento propio (`isolate`, `relative`) y el contenido va en flujo normal.
 */
export const SceneCases = ({ language = "es" as Lang }: { language?: Lang }) => {
  const [open, setOpen] = useState(false);
  const c = t[language];
  const routes = routesFor(language);

  return (
    <section className="v3-section bg-[color:var(--v3-mineral)]" aria-label={c.aria} id="casos">
      <div className="v3-shell">
        <SceneHead index="03" label={c.label} title={c.title} intro={c.intro} />

        {/* CASO 01 — Amazon / abrasivos */}
        <article className="relative isolate mt-14 grid gap-10 border-t border-[color:var(--v3-line)] pt-12 lg:grid-cols-12 lg:gap-14">
          <Reveal className="lg:col-span-5">
            <p className="v3-micro">
              <span style={{ color: "var(--v3-blue)" }}>{c.c1kicker}</span> · {c.c1place}
            </p>
            <h3 className="v3-display mt-4" style={{ fontSize: "clamp(1.5rem, 2.4vw, 2.1rem)" }}>
              {c.c1title}
            </h3>
            <p className="v3-body mt-5">
              <strong>{c.c1problem}</strong> {c.c1problemText}
            </p>
            <p className="v3-body mt-4">
              <strong>{c.c1decisions}</strong> {c.c1decisionsText}
            </p>

            <div className="mt-8 border-l-2 pl-5" style={{ borderColor: "var(--v3-blue)" }}>
              <p className="v3-micro">{c.c1resultLabel}</p>
              <p
                className="v3-display mt-2"
                style={{ fontSize: "clamp(1.6rem, 3vw, 2.4rem)", fontWeight: 700 }}
              >
                {c.c1figure}
              </p>
              <p className="mt-2 text-[0.95rem] leading-relaxed text-[color:var(--v3-ink-soft)]">
                {c.c1figureNote}
              </p>
            </div>

            <p className="v3-note mt-5">{c.c1disclaimer}</p>

            <Link to={routes.caseStudies} className="v3-inline-link mt-7">
              {c.allCases}
              <ArrowRight className="h-4 w-4" aria-hidden />
            </Link>
          </Reveal>

          <div className="lg:col-span-7">
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <button
                  type="button"
                  className="group relative block w-full bg-white p-3 text-left shadow-[0_18px_50px_-30px_rgba(13,31,60,0.5)] transition-shadow duration-200 hover:shadow-[0_22px_60px_-28px_rgba(13,31,60,0.55)]"
                >
                  <img
                    src={amazonSales}
                    alt={c.c1imgAlt}
                    className="w-full"
                    width={1200}
                    height={700}
                    loading="lazy"
                    decoding="async"
                  />
                  <span className="v3-micro absolute right-5 top-5 inline-flex items-center gap-2 bg-[color:var(--v3-ink)] px-3 py-2 text-white">
                    <Maximize2 className="h-3.5 w-3.5" aria-hidden />
                    {c.zoom}
                  </span>
                </button>
              </DialogTrigger>
              <DialogContent className="max-w-5xl bg-white p-4">
                <DialogTitle className="sr-only">{c.c1imgZoomTitle}</DialogTitle>
                <img src={amazonSales} alt={c.c1imgZoomAlt} className="h-auto w-full" />
              </DialogContent>
            </Dialog>
            <p className="v3-caption mt-3">{c.c1caption}</p>

            <figure className="mt-8 flex items-start gap-5 border-t border-[color:var(--v3-line)] pt-6">
              <img
                src={amazonProducts}
                alt={c.c1prodAlt}
                className="w-[38%] max-w-[240px] shrink-0"
                width={600}
                height={400}
                loading="lazy"
                decoding="async"
              />
              <figcaption className="v3-caption">{c.c1prodCaption}</figcaption>
            </figure>
          </div>
        </article>

        {/* CASO 02 — El Venado Foods */}
        <article className="relative isolate mt-16 grid gap-10 border-t border-[color:var(--v3-line)] pt-12 lg:grid-cols-12 lg:gap-14">
          <figure className="lg:col-span-6 lg:col-start-1 lg:row-start-1">
            <img
              src={venadoProducts}
              alt={c.c2imgAlt}
              className="w-full bg-white"
              width={1200}
              height={900}
              loading="lazy"
              decoding="async"
            />
            <figcaption className="v3-caption mt-3">{c.c2caption}</figcaption>
          </figure>

          <Reveal className="lg:col-span-6 lg:col-start-7 lg:row-start-1 lg:row-span-2">
            <p className="v3-micro">
              <span style={{ color: "var(--v3-blue)" }}>{c.c2kicker}</span> · {c.c2place}
            </p>
            <h3 className="v3-display mt-4" style={{ fontSize: "clamp(1.5rem, 2.4vw, 2.1rem)" }}>
              {c.c2title}
            </h3>
            <p className="v3-body mt-5">
              <strong>{c.c2lead}</strong> {c.c2leadText}
            </p>
            <ul className="mt-6 space-y-3 border-t border-[color:var(--v3-line)] pt-5">
              {c.c2points.map((d) => (
                <li
                  key={d}
                  className="flex gap-4 text-[1rem] leading-relaxed text-[color:var(--v3-ink-soft)]"
                >
                  <span
                    aria-hidden
                    className="mt-3 h-px w-5 shrink-0"
                    style={{ background: "var(--v3-copper)" }}
                  />
                  {d}
                </li>
              ))}
            </ul>
            <p className="v3-note mt-6">{c.c2note}</p>
          </Reveal>

          <figure className="border-t border-[color:var(--v3-line)] pt-6 lg:col-span-6 lg:col-start-1 lg:row-start-2">
            <img
              src={venadoStore}
              alt={c.c2webAlt}
              className="w-full"
              width={1200}
              height={800}
              loading="lazy"
              decoding="async"
            />
            <figcaption className="v3-caption mt-3">{c.c2webCaption}</figcaption>
          </figure>
        </article>
      </div>
    </section>
  );
};
